/***
 *
 *   VIEW ORDER
 *   View a sales order
 *
 **********/

import React, {Fragment, useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {AppLayoutContext, AuthContext, Button, Form, Icon, Loader, ReactTable, useAPI} from 'components/lib';
import {ViewContext} from "../../components/view/view";
import axios from "axios";
import {CreateDataSpecModal} from "../dataspec/shared/CreateDataSpecModal";
import {ImportFactoryModal} from "../dataspec/shared/ImportFactoryModal";
import {ExportClientDataModal} from "../dataspec/shared/ExportClientDataModal"
import Style from "../../components/form/checkbox/checkbox.tailwind";
import {ImportBundleIDModal} from "../dataspec/shared/ImportBundleIDModal";
import fileDownload from "js-file-download";
import ConfirmationModal from "../dataspec/shared/ConfirmationModal";
import { pdf } from "@react-pdf/renderer";
import { OrderPdf } from "./OrderPdf";
import {SendOrderSummaryModal} from "../dataspec/shared/SendOrderSummaryModal";

function DisplayProperties(props) {

  return (<div>
    {
      Object.keys(props.data).map(function(key) {
        return <div className="text-xs" key={key}>
          <strong>{key} : </strong>
          { props.data[key] }
        </div>
      })
    }
  </div>);
}

export function ViewOrder(props){

  const viewContext = useContext(ViewContext);
  const authContext = useContext(AuthContext);
  let navigate = useNavigate();
  const { orderId } = useParams();
  const customers = useAPI('/api/customer/');
  const users = useAPI('/api/user/');
  // loaded here so the generated PDFs have these lookups available
  // (useAPI inside the PDF component doesn't resolve during toBlob)
  const transponderModels = useAPI('/api/transponderModels/');
  const printTypes = useAPI('/api/printTypes/');
  const dataRecordTypes = useAPI('/api/dataRecordTypes/');
  const uidFormats = useAPI('/api/uidFormats/');
  const dataListSources = useAPI('/api/dataListSources/');
  const orderFromApi = useAPI('/api/orders/' + orderId);
  const orderItemsFromApi = useAPI('/api/orderItem/' + orderId);
  const [orderData, setOrderData] = useState(orderFromApi.data);
  const [orderItemData, setOrderItemData] = useState(orderItemsFromApi.data);
  const [openDataSpecModal, setOpenDataSpecModal] = useState(false);
  const [openImportFactoryModal, setOpenImportFactoryModal] = useState(false);
  const [openExportClientDataModal, setOpenExportClientDataModal] = useState(false);
  const [exportFileName, setExportFileName] = useState('');
  const [packageApprovalModal, setPackageApprovalModal] = useState(false);
  const [packageApproval, setApprovalPackage] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isDownloadingPackage, setIsDownloadingPackage] = useState(false);
  const [packageDownloaded, setPackageDownloaded] = useState(false);
  const [downloadPackageLocal, setDownloadPackageLocal] = useState(false);

  const [openImportBundleIDModal, setOpenImportBundleIDModal] = useState(false);
  const [orderItemId , setOrderItemId] = useState(false);
  const [customerOrdersId , setCustomerOrdersId] = useState(false);
  const [isStandalone , setIsStandalone] = useState(false);
  const [openResetModal, setOpenResetModal] = useState(false);
  const [openDeleteOrderModal, setOpenDeleteOrderModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [dataSpecs, setDataSpecs] = useState([]);


  // Add buttons to nav header (needed even if empty, to clear stale buttons from prev pages)
  const [navButtons, setNavButtons] = useContext(AppLayoutContext);
  useEffect(() => {
    setNavButtons([]);
  }, []);

  // Delete Order click: if any line item still has generated CAIDs,
  // ask the user to delete those first instead of deleting the order
  function handleDeleteOrderClick() {

    const itemsWithCaids = (orderItemData || []).filter(i => i.generation_config_id);

    if (itemsWithCaids.length) {
      viewContext.notification.show(
          'Please delete the generated data related to this order first.',
          'error',
          false
      );
      return;
    }

    setOpenDeleteOrderModal(true);
  }

  useEffect(() => {
    async function fetchDataSpecs() {

      if (!orderItemData) return;

      // Remove duplicate ids
      const ids = [...new Set(
          orderItemData
              .filter(i => i.data_spec_id)
              .map(i => i.data_spec_id)
      )];

      if (ids.length === 0) {
        setDataSpecs([]);
        return;
      }

      try {

        const requests = ids.map(async (id) => {

          const [specRes, columnsRes] = await Promise.all([
            axios.get(`/api/dataspec/${id}`),
            axios.get(`/api/dataSpec/${id}/dataListColumns`)
          ]);

          const spec = specRes.data.data;

          return {
            ...spec,
            dataListColumns: columnsRes.data.data
          };

        });

        const specs = await Promise.all(requests);

        setDataSpecs(specs);

      } catch (err) {
        console.error("Failed loading dataspecs", err);
      }

    }

    fetchDataSpecs();

  }, [orderItemData]);

  useEffect(() => {
    async function asyncFunction() {
      if (orderFromApi.data) {
        let now = new Date();
        let syncDate = new Date(orderFromApi.data.updated_at);
        let diffMins = (now - syncDate) / 60000;

        // approved either via the app (approved_source) or directly in
        // vTiger (data status is Approved or any later stage)
        const isApproved = (order) => !!order?.approved_source ||
            ['Approved', 'In Progress', 'All Jobs Complete', 'Final Data Package Delivered (Complete)']
                .includes(order?.vtiger_datastatus);

        setApprovalPackage(isApproved(orderFromApi.data));
        setPackageDownloaded(!!orderFromApi.data.package_downloaded_at);

        if (diffMins > 15 && orderFromApi.data.vtiger_sostatus!=='Cancelled') {
          const response = await axios.get('/api/orders/sync/' + orderId);
          setOrderData(response.data.data);
          setApprovalPackage(isApproved(response.data.data));
          setPackageDownloaded(!!response.data.data.package_downloaded_at);

          const itemsResponse = await axios.get('/api/orderItem/' + orderId);
          setOrderItemData(itemsResponse.data.data);
        } else {
          setOrderData(orderFromApi.data);
          setOrderItemData(orderItemsFromApi.data); // ✅ REQUIRED
        }
      }
    }
    asyncFunction();
  }, [orderFromApi, orderItemsFromApi.data]);

  // The realized-data import finishes in the background on the server,
  // so poll the item status until it flips (max ~2 min) — this turns the
  // buttons green without needing a manual page refresh
  const refreshOrderItems = React.useCallback(async () => {
    try {
      // fetch items FIRST — that endpoint recomputes the order's data
      // status, so the order fetch afterwards returns the fresh status
      const itemsRes = await axios.get(`/api/orderItem/${orderId}`);
      const orderRes = await axios.get(`/api/orders/${orderId}`);

      console.log("ORDER STATUS FROM API:", JSON.stringify(orderRes.data));

      setOrderItemData(itemsRes.data.data);
      setOrderData(orderRes.data.data);

    } catch (error) {
      console.error("Failed to refresh order data:", error);
    }
  }, [orderId]);

  const pollRealizedStatus = React.useCallback((itemId) => {

    let attempts = 0;

    const interval = setInterval(async () => {

      attempts++;

      try {

        const itemsRes = await axios.get(`/api/orderItem/${orderId}`);
        setOrderItemData(itemsRes.data.data);

        const target = (itemsRes.data.data || []).find(i => i.id === itemId);

        if ((target && target.importRealized) || attempts >= 24) {
          clearInterval(interval);

          // import finished → the order's data status may have advanced
          // (e.g. All Jobs Complete) — refresh so buttons like Download
          // Data Package appear without a page reload
          if (target && target.importRealized)
            await refreshOrderItems();
        }

      } catch (error) {

        clearInterval(interval);
      }
    }, 5000);
  }, [orderId, refreshOrderItems]);


  const handleDownloadDataPackage = async (summary) => {

    try {

      setIsDownloadingPackage(true);

      // 1️⃣ Generate Order PDF
      const blob = await pdf(
          <OrderPdf
              order={orderData}
              items={orderItemData}
              dataSpecs={dataSpecs}
              customers={customers}
              users={users}
              transponderModels={transponderModels}
              printTypes={printTypes}
              dataRecordTypes={dataRecordTypes}
              uidFormats={uidFormats}
              dataListSources={dataListSources}
          />
      ).toBlob();

      // 2️⃣ Send PDF to backend
      const DRIVE_FOLDER_ID = orderData.files_link.match(/\/folders\/([^/?]+)/)[1];
      const customerPo = orderData.vtiger_purchaseorder
          ? `_${String(orderData.vtiger_purchaseorder).replaceAll(' ', '_')}`
          : '';
      const fileName = `Data_Package_${orderData.vtiger_salesorder_no}${customerPo}.zip`;
      const formData = new FormData();
      formData.append("orderPdf", blob, "order.pdf");
      formData.append("fileName", fileName);
      formData.append("driveFolderId", DRIVE_FOLDER_ID);

      const response = await axios.post(
          `/api/orderItem/${orderId}/data-package`,
          formData,
          {
            responseType: "blob"
          }
      );

      // 3️⃣ Download ZIP locally only when requested — the Google Drive
      // copy is stored server-side either way
      if (downloadPackageLocal) {
        fileDownload(response.data, fileName);
      } else {
        viewContext.notification.show(
            'Data package has been saved on Google Drive.',
            'success',
            true
        );
      }

      setPackageDownloaded(true); // ✅ turn the button green

    } catch (error) {

      console.error("Failed to download data package:", error);
      alert("Failed to download data package");

    } finally {

      setIsDownloadingPackage(false);

    }

  };

  // Vtiger locks Closed/Cancelled orders — approval can't be changed on them
  const orderLocked = ['Closed', 'Cancelled'].includes(orderData?.vtiger_sostatus);

  const handleApprovePackage = async () => {
    if (orderLocked) return;

    const newApproval = !packageApproval;

    try {
      setIsApproving(true);
      setApprovalPackage(newApproval);

      await axios.post(
          `/api/orders/${orderId}/package-approval`,
          {
            approval: newApproval
          }
      );

      await refreshOrderItems();

      // ✅ Your notification system
      viewContext.notification.show(
          newApproval
              ? "Package approved successfully."
              : "Package unapproved successfully.",
          'success',
          true
      );

    } catch (error) {
      console.error("Approval failed:", error);

      // revert the optimistic toggle so the button reflects reality
      setApprovalPackage(!newApproval);

      viewContext.notification.show(
          error.response?.data?.message || "Failed to update package approval.",
          'error',
          true
      );

    } finally {
      setIsApproving(false);
    }
  };
  const downloadOrderSummary = async () => {
    try {
      const blob = await pdf(
          <OrderPdf
              order={orderData}
              items={orderItemData}
              dataSpecs={dataSpecs}
              customers={customers}
              users={users}
              transponderModels={transponderModels}
              printTypes={printTypes}
              dataRecordTypes={dataRecordTypes}
              uidFormats={uidFormats}
              dataListSources={dataListSources}
          />
      ).toBlob();

      const summaryFileName = `Order_Summary_${orderData.vtiger_salesorder_no}.pdf`;

      fileDownload(blob, summaryFileName);

      // also store a copy on Google Drive in the order's Factory Data
      // folder — background + non-fatal, the download already happened
      const formData = new FormData();
      formData.append('file', blob, summaryFileName);

      axios.post(`/api/driveFile/order/${orderId}/storeSummary`, formData)
          .catch((error) => {
            console.error("Failed to store order summary on Google Drive:", error);
          });

    } catch (error) {
      console.error("Failed to download order summary:", error);
      alert("Failed to download order summary");
    }
  };
  async function resetstatus(itemId) {
    try {


      const isResetDataStatus =
          orderItemData.filter(item => item.exportProduction).length === 1;

     await axios({
        method: 'post',
        url: '/api/orderItem/resetStatus/' + itemId,
        data: {orderId: orderId, isResetDataStatus: isResetDataStatus},
      });
      await refreshOrderItems();

    } catch (error) {
      console.error(error);
      alert("Failed to reset item states");
    }
  }


  function ItemNameCell({row, value}) {
    return (
        <div className={"flex flex-col"}>
          <div className="text-md font-bold text-gray-700">
            {row.original.product_name}
          </div>
          <div className={"text-sm text-gray-500 whitespace-pre-line"}>
            {row.original.details}
          </div>
        </div>);
  }

  function JobNameCell({row, value}) {
    const [isEditing, setIsEditing] = useState(false);
    const [jobName, setJobName] = useState(row.original.job_name);

    const handleEditClick = () => {
      setIsEditing(true);
    };

    const handleChange = (e) => {
      setJobName(e.target.value);
    };

    const handleBlur = async () => {
      setIsEditing(false);
      const response = await axios({
        method: 'post',
        url: '/api/orderItem/updateJobName/' + row.original.id,
        data: {val: jobName},
      });
      await refreshOrderItems();
    };

    return (
        <div className="flex flex-col">
          <div className="text-md text-gray-600 flex items-center gap-2">
            {isEditing ? (
                <input
                    type="text"
                    value={jobName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus
                    className="border border-gray-300 px-2 py-1 rounded"
                />
            ) : (
                <span>{jobName}</span>
            )}
            <div className="text-red-500 cursor-pointer" onClick={handleEditClick}>
              <Icon image="edit" size={18} />
            </div>
          </div>
        </div>
    );
  }

  function ItemQuantityCell({row, value}) {
    return (
        <div className={"flex flex-col"}>
          <div className="text-md text-left">
            {row.original.quantity}
          </div>

          {row.original.is_static ? '' :
              <Button
                  action={async () => {
                    setOrderItemId(row.original.id);
                    const jobName = row?.original?.job_name?.replace(/\./g, '_') || '';
                    // customer order number (their PO) is included when provided
                    const customerPo = orderFromApi.data.vtiger_purchaseorder
                        ? `${String(orderFromApi.data.vtiger_purchaseorder).replaceAll(' ', '_')}_`
                        : '';
                    setExportFileName(
                        `${orderFromApi.data.vtiger_salesorder_no}_${customerPo}LineItem${row.original.number}_${jobName.replaceAll(' ', '_')}_DELIVER_TO_CLIENT`
                    );
                    setOpenExportClientDataModal(true);
                  }}
                  icon="upload"
                  text="Export Data for Client"
                  xSmall
                  disabled={row.original.is_static === true || row.original.exportClient === true}
                  className={`mt-2 ${
                      row.original.exportClient
                          ? "bg-green-600 cursor-not-allowed"
                          : row.original.is_static
                              ? "bg-gray-300 cursor-not-allowed"
                              : "bg-orange-500 hover:bg-orange-700"
                  }`}
              />


          }

        </div>);
  }

  function DataSpecCell({ row, value }) {

    return (<>
      {!row.original.data_spec_id ?
          <Button
              icon={'link'}
              action={async () => {
                setOrderItemId(row.original.id);
                setOpenDataSpecModal(true);
              }} text='Link Data Spec' xSmall className={"bg-orange-500 hover:bg-orange-700"}/> :
          <div className={"flex gap-6 items-center"}>
            <a href={`/dataspec/${row.original.data_spec_id}`} className="hover:underline text-sm text-orange-500">
              {row.original.data_spec_number.split('_')[0]}
            </a>
            <span className={"cursor-pointer"} onClick={async () => {
              // Remove order item link to data spec
              const response = await axios({

                method: 'post',
                url: '/api/orderItem/unlink/' + row.original.id,
                data: {
                  dataSpecId: row.original.data_spec_id,
                },

              });

              // Refresh data for order item
              const itemsResponse = await axios({

                method: 'get',
                url: '/api/orderItem/' + row.original.order_id,

              });
              setOrderItemData(itemsResponse.data.data);
            }
            }>
            <Icon
                className='icon'
                image={'trash'}
                size={20}
                color={'red'}
            />
          </span>
          </div>
      }
    </>);

  }
  function IdGenCell({ row, value }) {

    if(row.original.is_static===true) {
      return (
          <div className={"flex gap-4 items-center"}>
            <div className={"text-sm text-gray-400 -mt-1"}>
              Static Data
              <br/>
              {row.original.payload_template}
            </div>
            {/* Refresh icon - only show if any status is true */}
            {(row.original.exportProduction ||
                row.original.exportClient ||
                row.original.importRealized ||
                row.original.importBundleIds) && (
                <span
                    className="cursor-pointer"
                    onClick={() => {
                      setSelectedItemId(row.original.id);
                      setCustomerOrdersId(row.original.order_id);
                      setOpenResetModal(true);
                    }}
                >
                  <Icon className="icon" image="rotate-ccw" size={18} color="red"/>
                </span>
            )}
          </div>
      );

    } else {
      if (row.original.data_list_source_id === 1 && !row.original.generation_config_id) {
        return (

              <div className={"flex gap-4 items-center"}>
                  <span
                      className={'text-sm text-orange-500 hover:underline cursor-pointer whitespace-pre-line'}
                      onClick={() => navigate(`/order/${orderId}/customerData/${row.original.id}`)}
                  >
                    Customer Provided
                  </span>
                    {/* Refresh icon - only show if any status is true */}
                    {(row.original.exportProduction ||
                        row.original.exportClient ||
                        row.original.importRealized ||
                        row.original.importBundleIds) && (
                        <span
                            className="cursor-pointer"
                            onClick={() => {
                              setSelectedItemId(row.original.id);
                              setCustomerOrdersId(row.original.order_id);
                              setOpenResetModal(true);
                            }}
                        >
                        <Icon className="icon" image="rotate-ccw" size={18} color="red"/>
                      </span>
                    )}
              </div>

        );
      }
      else{
        return (<>

          { !row.original.generation_config_id  ? row.original.uid_only ?
              <div className={"flex gap-4 items-center"}>
                <span className={'text-sm text-gray-500 whitespace-pre-line'}>UID only</span>
                {/* Refresh icon - only show if any status is true */}
                {(row.original.exportProduction ||
                    row.original.exportClient ||
                    row.original.importRealized ||
                    row.original.importBundleIds) && (
                    <span
                        className="cursor-pointer"
                        onClick={() => {
                          setSelectedItemId(row.original.id);
                          setCustomerOrdersId(row.original.order_id);
                          setOpenResetModal(true);
                        }}
                    >
                      <Icon className="icon" image="rotate-ccw" size={18} color="red"/>
                    </span>
                )}
              </div> :
              <Button
                  action={async () => {
                    navigate(`/order/${orderId}/generation/${row.original.id}?count=${row.original.quantity}&extra=${15}`);

                  }}
                  disabled={row.original.standalone_batchID !== null}
                  icon='settings'
                  text='Configure ID Gen' xSmall className={ row.original.standalone_batchID ? "bg-gray-300 cursor-not-allowed" : "bg-orange-500 hover:bg-orange-700"}/> :
              <div className={"flex gap-4 items-center"}>
                <div>
                  {row.original.idgencounts.map((idgc) => {
                    return (

                        <div className={"flex flex-col mb-2"} key={`${idgc.caid_scope_id}_${idgc.caid_batch_id}`}>
                          <a
                              href={`/customer/${orderFromApi.data.customer_id}/caid/${idgc.caid_scope_id}?batchId=${idgc.caid_batch_id}`}
                              className="hover:underline text-sm text-orange-500">
                            {(parseInt(idgc.count)/row.original.idgencounts.length).toLocaleString()} CAIDs generated
                          </a>
                          <div className={"text-sm text-gray-400 -mt-1"}>
                            {idgc.caid_scope_name}
                          </div>
                        </div>);

                  })
                  }
                </div>
                <span className={"cursor-pointer"} onClick={async () => {
                  // delete generation batches and generation config
                  const response = await axios({

                    method: 'post',
                    url: '/api/orderItem/deleteGeneration/' + row.original.id,
                    data: {},

                  });
                  await refreshOrderItems();
                }
                }>
                <Icon
                    className='icon'
                    image={'trash'}
                    size={20}
                    color={'red'}
                />
              </span>
                {/* Refresh icon - only show if any status is true */}
                {(row.original.exportProduction ||
                    row.original.exportClient ||
                    row.original.importRealized ||
                    row.original.importBundleIds) && (
                    <span
                        className="cursor-pointer"
                        onClick={() => {
                          setSelectedItemId(row.original.id);
                          setCustomerOrdersId(row.original.order_id);
                          setOpenResetModal(true);
                        }}
                    >
            <Icon className="icon" image="rotate-ccw" size={18} color="red" />
          </span>
                )}
              </div>
          }
        </>);
      }
    }

  }
  function FactoryExportCell({ row }) {
    const [isChecked, setIsChecked] = useState(false)

    const checkHandler = () => {
      setIsChecked(!isChecked)
    }
    return (<>
      <div className={'factory_operations'}>
        <div>
          { row.original.uid_only && !row.original.is_static ? '' :
          <Button
              action={async () => {
                navigate(`/order/${orderId}/factoryExport/${row.original.id}?dataSpecId=${row.original.data_spec_id}${row.original.generation_config_id ? '&generationConfigId=' + row.original.generation_config_id: ''}`);
              }}
              disabled={!row.original.data_spec_id || row.original.exportProduction}
              icon={'upload'}
              text='Export Data for Production'
              xSmall
              className={
                !row.original.data_spec_id
                    ? "bg-gray-300 cursor-not-allowed"
                    : row.original.exportProduction
                        ? "bg-green-600 cursor-not-allowed pointer-events-none"
                        : "bg-orange-500 hover:bg-orange-700 "
              }
          />
          }


          {row.original.standalone_batchID===null ?
              row.original.is_static ? '' :
              <>
                <Button
                    action={async () => {
                      setOrderItemId(row.original.id);
                      console.log("Row Data:" + JSON.stringify(row.original));

                      if (row.original.generation_config_id === null &&
                          row.original.data_file_upload_file_id === null) {
                        setIsStandalone(true);
                      } else {
                        setIsStandalone(false);
                      }

                      setOpenImportFactoryModal(true);
                    }}
                    icon="download"
                    text="Import Realized Data"
                    xSmall
                    disabled={row.original.is_static === true || row.original.importRealized === true}
                    className={`mt-2 ${
                        row.original.importRealized
                            ? "bg-green-600 cursor-not-allowed"
                            : row.original.is_static
                                ? "bg-gray-300 cursor-not-allowed"
                                : "bg-orange-500 hover:bg-orange-700"
                    }`}
                />

                <Button
                    action={async () => {
                      setOrderItemId(row.original.id);
                      setOpenImportBundleIDModal(true);
                    }}
                    icon="download"
                    text="Import Bundle IDs"
                    xSmall
                    disabled={row.original.is_static === true || row.original.importBundleIds === true}
                    className={`mt-2 ${
                        row.original.importBundleIds
                            ? "bg-green-600 cursor-not-allowed"
                            : row.original.is_static
                                ? "bg-gray-300 cursor-not-allowed"
                                : "bg-orange-500 hover:bg-orange-700"
                    }`}
                />

              </>

              : <Button
                  action={async () => {
                     // navigate('/customer/'+orderFromApi.data.customer_id+'/caid/b9949c1c-c62f-4699-8913-64bc926bf117')
                    window.open('/customer/'+orderFromApi.data.customer_id+'/caid/b9949c1c-c62f-4699-8913-64bc926bf117?batchId='+row.original.standalone_batchID+'&orderId='+orderId,'_blank')
                  }}
                  icon={'feather'}
                  text='Check Linked UID' xSmall
                  className='mt-2 bg-green-600 hover:bg-green-700'/>
          }


        </div>
      </div>

    </>);
  }

  function CollectChipUID({ row }) {
    let isCheckedInitial= false;
    if(row.original.collect_chip_uid || row.original.collect_uid){
      isCheckedInitial=true
    }
    const [isChecked, setIsChecked] = useState(isCheckedInitial)
    let isDisabled=false;
    if (row.original.collect_uid===true || row.original.is_static){
      isDisabled=true;
    }

    const checkHandler = async () => {
      setIsChecked(!isChecked)
      const response = await axios({
        method: 'post',
        url: '/api/orderItem/collectChipUid/' + row.original.id,
        data: {val: !isChecked},

      });

      // window.location.reload();

    }


    return (<>
      <div className={'factory_operations'}>
        <div className={'collect-chip-uid'}>
          <input
              type="checkbox"
              id="collect-chip-uid"
              className={ Style.input }
              checked={isChecked}
              disabled= {isDisabled}
              onChange={checkHandler}
          />
        </div>
      </div>

    </>);
  }

  const columns = React.useMemo(() => [
    {
      Header: "Item Number",
      accessor: 'number',
    },
    {
      Header: "Job Name",
      accessor: 'job_name',
      Cell: JobNameCell,
    },
    {
      Header: "Data Spec",
      accessor: 'id',
      Cell: DataSpecCell,
    },
    {
      Header: "CAID Generation",
      accessor: 'generation_config_id',
      Cell: IdGenCell,
    },
    {
      Header: "Factory Data",
      Cell: FactoryExportCell,
    },
    {
      Header: "Collect UID",
      Cell: CollectChipUID,
    },
    {
      Header: "Item Name",
      accessor: 'details',
      Cell: ItemNameCell,
    },
    {
      Header: "Quantity",
      accessor: 'quantity',
      Cell: ItemQuantityCell,
    },
  ], [orderItemData])

  console.log("Sdds"+JSON.stringify(orderItemData));

  return (
      <Fragment>

        { orderItemData == null || orderItemsFromApi.loading || orderData == null || customers.data == null ?
            <Loader /> :
            <>
              <div className={"flex"}>
                <div className={"p-4 bg-white m-4 shadow sm:rounded-lg"}>
                  <h3 className={"text-xl font-semibold mb-4 leading-6 text-gray-900"}>Details</h3>
                  <div className={"flex gap-8"}>
                    <div  className={"flex-col mt-2"}>
                      <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>
                        Sales Order Number
                      </div>
                      <div className={"text-gray-500"}>
                        {orderData.vtiger_salesorder_no}
                      </div>
                    </div>
                    {orderData.vtiger_purchaseorder &&
                        <div className={"flex-col mt-2"}>
                          <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>
                            Customer Order Number
                          </div>
                          <div className={"text-gray-500"}>
                            {orderData.vtiger_purchaseorder}
                          </div>
                        </div>
                    }
                    <div  className={"flex-col mt-2"}>
                      <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>
                        Organization
                      </div>
                      <a href={`/customer/${orderData.customer_id}`} className="hover:underline text-sm text-orange-500">
                        {customers.data ? customers.data.find(u => u.id === orderData.customer_id).name: orderData.customer_id}
                      </a>
                    </div>
                    <div  className={"flex-col mt-2"}>
                      <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>
                        Status
                      </div>
                      <div className={"text-gray-500"}>
                        {orderData.vtiger_sostatus}
                      </div>
                    </div>
                    <div className={"flex-col mt-2"}>
                      <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>
                        Last Sync Time
                      </div>
                      <div className={"text-gray-500"}>
                        {new Date(orderData.updated_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className={"flex gap-8"}>
                    <div  className={"flex-col mt-2"}>
                      <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>
                        Vtiger Link
                      </div>
                      <a className={"relative block text-sm hover:underline text-orange-500"}
                         target="_blank"
                         href={orderData.external_link}>
                        {orderData.external_link}
                      </a>
                    </div>
                  </div>
                  <div className={"flex gap-8"}>
                    <div  className={"flex-col mt-2"}>
                      <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>
                        Google Drive Link
                      </div>
                      {orderData.files_link ? <a className={"relative block text-sm hover:underline text-orange-500"}
                                                 target="_blank"
                                                 href={orderData.files_link}>
                        {orderData.files_link}
                      </a> : '-'}
                    </div>
                  </div>

                  <div className={"flex gap-8"}>
                    <div  className={"flex-col mt-2"}>
                      <div className={"relative block text-sm mb-0 uppercase text-gray-700"}>
                        Data Status
                      </div>
                      <div className={"text-gray-500"}>
                        {orderData.vtiger_datastatus ? orderData.vtiger_datastatus : '-'}
                      </div>

                    </div>
                  </div>
                </div>
              </div>


              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold mb-4 mt-6 leading-6 text-gray-900">
                  Line Items
                </h3>

                <div className={"flex gap-8 justify-between items-start"}>

                  <div className="relative group">
                    <Button
                        text={
                          isApproving
                              ? "Processing..."
                              : packageApproval
                                  ? "Approved"          // ✅ show state, not action
                                  : "Approve Package"
                        }
                        icon={
                          isApproving
                              ? "loader"
                              : packageApproval
                                  ? "check-circle"
                                  : "check"
                        }
                        action={handleApprovePackage}
                        disabled={isApproving || orderLocked}
                        xSmall
                        className={`py-4 ${
                            isApproving
                                ? "bg-gray-400 cursor-not-allowed animate-pulse"
                                : orderLocked
                                    ? (packageApproval ? "bg-green-600" : "bg-gray-400") + " cursor-not-allowed opacity-70"
                                    : packageApproval
                                        ? "bg-green-600 hover:bg-green-700"   // ✅ approved state
                                        : "bg-orange-500 hover:bg-orange-700" // ✅ action state
                        }`}
                    />
                    {!isApproving && (orderLocked || packageApproval) &&
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                        {orderLocked
                            ? `Order is ${orderData.vtiger_sostatus} — approval can't be changed`
                            : "Press again to remove approval"}
                      </span>
                    }
                  </div>
                  { !packageApproval && (orderData.vtiger_datastatus==="Created" || orderData.vtiger_datastatus==="Package Approval Requested") ?
                    <>

                    <Button
                        text="Send Package Approval Request"
                        icon="send"
                        action={() => setPackageApprovalModal(true)}
                        xSmall
                        className="bg-orange-500 hover:bg-orange-700 py-4"
                    />
                    <Button
                      text="Download Order Summary"
                      icon="download"
                      action={downloadOrderSummary}
                      xSmall
                      className="bg-orange-500 hover:bg-orange-700 py-4"
                    />
                    </>
                    : ''}

                { orderData.vtiger_datastatus==="All Jobs Complete" || orderData.vtiger_datastatus==="Final Data Package Delivered (Complete)" ?
                    <div className="flex items-stretch">
                      <Button
                          text={
                            isDownloadingPackage
                                ? "Preparing..."
                                : packageDownloaded
                                    ? "Data Package Downloaded"
                                    : "Download Data Package"
                          }
                          icon={
                            isDownloadingPackage
                                ? "loader"
                                : packageDownloaded
                                    ? "check-circle"
                                    : "download"
                          }
                          action={handleDownloadDataPackage}
                          disabled={isDownloadingPackage}
                          xSmall
                          className={`py-4 rounded-r-none ${
                              isDownloadingPackage
                                  ? "bg-gray-400 cursor-not-allowed animate-pulse"
                                  : packageDownloaded
                                      ? "bg-green-600 hover:bg-green-700"
                                      : "bg-orange-500 hover:bg-orange-700"
                          }`}
                      />
                      <div className="relative group flex">
                        <button
                            type="button"
                            disabled={isDownloadingPackage}
                            onClick={() => setDownloadPackageLocal(!downloadPackageLocal)}
                            className={`px-2.5 rounded rounded-l-none border-l border-white/40 flex items-center justify-center transition-colors ${
                                isDownloadingPackage
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : packageDownloaded
                                        ? "bg-green-600 hover:bg-green-700"
                                        : "bg-orange-500 hover:bg-orange-700"
                            }`}
                        >
                          <span className={downloadPackageLocal ? "" : "opacity-40"}>
                            <Icon image="monitor" size={16} color="white"/>
                          </span>
                        </button>
                        <span className="absolute bottom-full right-0 mb-2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                          {downloadPackageLocal
                              ? "Local download: On"
                              : "Local download: Off"}
                        </span>
                      </div>
                    </div>
                    : ''}

                { authContext.permission?.admin &&
                    <Button
                        text="Delete Order"
                        icon="trash"
                        action={handleDeleteOrderClick}
                        xSmall
                        className="bg-red-600 hover:bg-red-700 py-4"
                    />
                }

                </div>
              </div>


              <ReactTable
                  columns={columns}
                  data={orderItemData}
                  storageKey="viewOrder.lineItems.pageSize"
              />

              <SendOrderSummaryModal
                  open={packageApprovalModal}
                  setOpen={setPackageApprovalModal}
                  orderId={orderId}
                  orderData={orderData}
                  orderItemData={orderItemData}
                  dataSpecs={dataSpecs}
                  customers={customers}
                  users={users}
                  transponderModels={transponderModels}
                  printTypes={printTypes}
                  dataRecordTypes={dataRecordTypes}
                  uidFormats={uidFormats}
                  dataListSources={dataListSources}
              />
              <CreateDataSpecModal open={openDataSpecModal}
                                   setOpen={setOpenDataSpecModal}
                                   afterCreate={(data) => {
                                     navigate(`/dataSpec/${data.id}`);
                                   }}
                                   linkCallback={async (dsId) => {
                                     const response = await axios({

                                       method: 'post',
                                       url: '/api/orderItem/link/' + orderItemId,
                                       data: {data: dsId},

                                     });
                                     setOpenDataSpecModal(false);

                                     const itemsResponse = await axios({

                                       method: 'get',
                                       url: '/api/orderItem/' + orderId,

                                     });
                                     setOrderItemData(itemsResponse.data.data);
                                   }}
                                   orderItemId={orderItemId}
                                   customerId={orderData.customer_id}
                                   allowExistingLink={true}
                                   requestType={''}
              />

              <ExportClientDataModal open={openExportClientDataModal}
                                  setOpen={setOpenExportClientDataModal}
                                  onSuccess={refreshOrderItems}
                                  afterCreate={(data) => {
                                    navigate(`/dataSpec/${data.id}`);
                                  }}
                                  linkCallback={async (dsId) => {
                                    const response = await axios({
                                      method: 'post',
                                      url: '/api/orderItem/link/' + orderItemId,
                                      data: {data: dsId},
                                    });
                                    setOpenExportClientDataModal(false);

                                    await refreshOrderItems();
                                  }}
                                  orderItemId={orderItemId}
                                  customerId={orderData.customer_id}
                                  allowExistingLink={true}
                                  exportFileName={exportFileName}
              />

              <ImportFactoryModal open={openImportFactoryModal}
                                  setOpen={setOpenImportFactoryModal}
                                  onSuccess={async () => {
                                    await refreshOrderItems();
                                    pollRealizedStatus(orderItemId);
                                  }}
                                  afterCreate={(data) => {
                                    navigate(`/dataSpec/${data.id}`);
                                  }}
                                  linkCallback={async (dsId) => {
                                    const response = await axios({
                                      method: 'post',
                                      url: '/api/orderItem/link/' + orderItemId,
                                      data: {data: dsId},
                                    });
                                    setOpenImportFactoryModal(false);

                                    await refreshOrderItems();
                                  }}
                                  orderItemId={orderItemId}
                                  customerId={orderData.customer_id}
                                  allowExistingLink={true}
                                  isStandalone={isStandalone}
                                  customerOrdersId={customerOrdersId}
              />

              <ImportBundleIDModal open={openImportBundleIDModal}
                                  setOpen={setOpenImportBundleIDModal}
                                  onSuccess={refreshOrderItems}
                                  afterCreate={(data) => {
                                    navigate(`/dataSpec/${data.id}`);
                                  }}
                                  linkCallback={async (dsId) => {
                                    const response = await axios({

                                      method: 'post',
                                      url: '/api/orderItem/link/' + orderItemId,
                                      data: {data: dsId},

                                    });
                                    await refreshOrderItems();
                                    setOpenImportBundleIDModal(false);
                                  }}
                                  orderItemId={orderItemId}
                                  customerId={orderData.customer_id}
                                  allowExistingLink={true}
                                  isStandalone={isStandalone}
                                  customerOrdersId={customerOrdersId}
              />

              <ConfirmationModal
                  open={openResetModal}
                  setOpen={setOpenResetModal}
                  title="Reset All Status?"
                  message="This action will reset all export/import statuses for this line item."
                  icon="rotate-ccw"
                  iconColor="red"
                  confirmText="Reset"
                  cancelText="Cancel"
                  onConfirm={async () => {
                    await resetstatus(selectedItemId);
                  }}
              />

              <ConfirmationModal
                  open={openDeleteOrderModal}
                  setOpen={setOpenDeleteOrderModal}
                  title="Delete Order?"
                  message={`This will permanently delete order ${orderData.vtiger_salesorder_no} and all of its line items. This action cannot be undone.`}
                  icon="trash"
                  iconColor="red"
                  confirmText="Delete"
                  cancelText="Cancel"
                  onConfirm={async () => {

                    try {

                      await axios.delete('/api/orders/' + orderId);

                      viewContext.notification.show(
                          'Order deleted successfully.',
                          'success',
                          true
                      );

                      // go to the re-import page so the order can be synced again from vTiger
                      orderData.vtiger_id ?
                          navigate('/rorder/' + orderData.vtiger_id) :
                          navigate('/orders');

                    } catch (error) {

                      console.error("Delete order failed:", error);

                      // autoclose false: stays visible until the user dismisses it
                      viewContext.notification.show(
                          error.response?.data?.message || 'Failed to delete order.',
                          'error',
                          false
                      );
                    }
                  }}
              />

            </>
        }
      </Fragment>
  )
}
